/**
 * PackEdge public API client (REST, browser-side, zero dependencies).
 *
 * Licensing (validate/activate/deactivate), event/feature-usage tracking, and
 * feedback all go through PackEdge's `/public/v1/*` endpoints, which allow
 * cross-origin browser calls and use a non-secret product `public_key`.
 *
 * Config is injected by PHP via wp_localize_script as `packedge`:
 *   window.ajaxpress_admin_vars.packedge  (admin SPA)
 *   window.ajaxpress_vars.packedge        (frontend)
 *     { public_key: 'pk_...', api: 'https://api.packedge.dev', slug: 'ajaxpress' }
 *
 * Pricing/checkout intentionally stays on arraystory.com — this client only
 * owns licensing + telemetry.
 */

function vars() {
	if (typeof window === 'undefined') return {};
	return window.ajaxpress_admin_vars || window.ajaxpress_vars || {};
}

function cfg() {
	const p = (vars().packedge) || {};
	return {
		publicKey: p.public_key || '',
		base: (p.api || 'https://api.packedge.dev').replace(/\/+$/, ''),
		slug: p.slug || 'ajaxpress',
	};
}

/** Normalised host for the current site (domain only). */
function siteHost(explicit) {
	const url = explicit || vars()?.site?.url || (typeof window !== 'undefined' ? window.location.href : '');
	try {
		return new URL(url).host;
	} catch (e) {
		return typeof window !== 'undefined' ? window.location.host : '';
	}
}

async function post(path, body) {
	const { base } = cfg();
	try {
		const res = await fetch(base + path, {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify(body),
		});
		let data = {};
		try {
			data = await res.json();
		} catch (e) {
			/* non-JSON */
		}
		return { ok: res.ok, status: res.status, data };
	} catch (e) {
		return { ok: false, networkError: true, data: {} };
	}
}

/**
 * Map a PackEdge license object to the legacy `ajaxpress_license` option shape
 * the rest of the plugin already understands. Plan name/type are derived from
 * seats + expiry so the UI keeps showing "Personal/Agency · Yearly/Lifetime"
 * without the validate endpoint needing to return plan metadata.
 */
function mapLicense(key, lic, product) {
	const seats = typeof lic.seats === 'number' ? lic.seats : 1;
	const used = typeof lic.activatedCount === 'number' ? lic.activatedCount : 0;
	const lifetime = !lic.expiresAt;
	const tier = seats <= 1 ? 'Personal' : 'Agency';
	const period = lifetime ? 'Lifetime' : 'Yearly';
	return {
		key,
		status: lic.status === 'active' ? 'active' : (lic.status || 'inactive'),
		plan_name: tier + ' ' + period,
		plan_type: lifetime ? 'lifetime' : 'yearly',
		activations: used,
		activation_limit: seats,
		activations_remaining: Math.max(0, seats - used),
		expires_at: lic.expiresAt || 'never',
		subscription_status: lic.status || 'active',
		product_name: (product && product.name) || 'AjaxPress',
	};
}

/** Validate a license for this site. Returns the legacy-shaped result. */
export async function validate(key, site) {
	const { publicKey } = cfg();
	const r = await post('/public/v1/licenses/validate', {
		public_key: publicKey,
		license_key: key,
		site: siteHost(site),
	});
	if (r.networkError) {
		return { success: false, networkError: true, message: 'Failed to verify license. Please try again.' };
	}
	if (r.data && r.data.valid && r.data.license) {
		return { success: true, valid: true, message: 'License valid.', license: mapLicense(key, r.data.license, r.data.product) };
	}
	return { success: false, invalid: true, message: (r.data && r.data.error) || 'Invalid license key.' };
}

/** Activate (bind this site, consuming a seat), then return full details. */
export async function activate(key, site) {
	const { publicKey } = cfg();
	const host = siteHost(site);
	const r = await post('/public/v1/licenses/activate', {
		public_key: publicKey,
		license_key: key,
		site: host,
	});
	if (r.networkError) {
		return { success: false, networkError: true, message: 'Failed to reach the license server. Please try again.' };
	}
	if (!(r.data && (r.data.success || r.data.site_id))) {
		return { success: false, invalid: true, message: (r.data && r.data.error) || 'Activation failed.' };
	}
	// Site is bound; fetch the authoritative license details to store/display.
	return await validate(key, host);
}

/** Deactivate this site (frees a seat). Best-effort; never throws. */
export async function deactivate(key, site) {
	const { publicKey } = cfg();
	const r = await post('/public/v1/licenses/deactivate', {
		public_key: publicKey,
		license_key: key,
		site: siteHost(site),
	});
	return { success: !r.networkError && !(r.data && r.data.error) };
}

/** Whether the user has opted in to usage analytics (diagnostic consent). */
export function consentAllowed() {
	return (vars()?.diagnostic_permission || '') === 'allowed';
}

/**
 * Fire-and-forget event (install/uninstall/feature usage). Uses sendBeacon when
 * available so it survives page unload; falls back to keepalive fetch.
 *
 * Feature/usage events require opt-in (consent). Pass opts.lifecycle = true for
 * install/uninstall counts, which always send (no personal/site data).
 */
export function track(event, properties, opts) {
	const { publicKey, base, slug } = cfg();
	if (!publicKey || !event) return false;
	if (!(opts && opts.lifecycle) && !consentAllowed()) return false;
	const payload = {
		public_key: publicKey,
		event: event,
		slug: slug,
		site: siteHost(),
		license_key: (opts && opts.licenseKey) || undefined,
		properties: properties || {},
	};
	const url = base + '/public/v1/event';
	try {
		if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
			const blob = new Blob([JSON.stringify(payload)], { type: 'text/plain' });
			if (navigator.sendBeacon(url, blob)) return true;
		}
	} catch (e) {
		/* fall through */
	}
	try {
		fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload), keepalive: true }).catch(function () {});
	} catch (e) {
		/* ignore */
	}
	return true;
}

/** Submit feedback (sentiment/rating/message). Returns {ok,data}. */
export async function feedback(payload) {
	const { publicKey, slug } = cfg();
	return await post('/public/v1/feedback', Object.assign({ public_key: publicKey, slug: slug, site: siteHost() }, payload || {}));
}

// Return params PackEdge appends to return_url after a purchase. We declare our
// own names at checkout so they don't collide with any other PackEdge product.
export const ACTIVATE_PARAM = 'ajaxpress_activate';
export const PENDING_PARAM = 'ajaxpress_pending';

/**
 * Fetch the product's public pricing plans from PackEdge. Each plan is returned
 * with a ready-to-use `checkout_link` (PackEdge hosted checkout → Polar/Stripe)
 * carrying our activation params; the caller appends `&return_url=<page>` so the
 * license is shipped straight back for one-click activation.
 */
export async function getPlans() {
	const { publicKey, base } = cfg();
	if (!publicKey) return { success: false, plans: [] };
	try {
		const res = await fetch(base + '/public/v1/plans?public_key=' + encodeURIComponent(publicKey));
		const data = await res.json();
		if (!data || !Array.isArray(data.plans)) return { success: false, plans: [] };
		const email = vars()?.site?.admin_email || '';
		const plans = data.plans.map(function (p) {
			let link = base + '/public/v1/checkout?public_key=' + encodeURIComponent(publicKey) +
				'&plan=' + encodeURIComponent(p.slug) +
				'&activate_param=' + ACTIVATE_PARAM + '&pending_param=' + PENDING_PARAM;
			if (email) link += '&email=' + encodeURIComponent(email);
			return Object.assign({}, p, { checkout_link: link });
		});
		return { success: true, plans: plans };
	} catch (e) {
		return { success: false, plans: [] };
	}
}

/**
 * Read a post-purchase activation result from the current URL and strip the
 * params. Returns { key } on success, { pending: true } while provisioning, or
 * null. Also accepts the legacy `license_key` param for back-compat.
 */
export function consumeActivationFromUrl() {
	if (typeof window === 'undefined') return null;
	const params = new URLSearchParams(window.location.search);
	const key = params.get(ACTIVATE_PARAM) || params.get('license_key');
	const pending = params.get(PENDING_PARAM);
	params.delete(ACTIVATE_PARAM);
	params.delete(PENDING_PARAM);
	params.delete('license_key');
	const query = params.toString();
	window.history.replaceState({}, document.title,
		window.location.pathname + (query ? '?' + query : '') + window.location.hash);
	if (key) return { key: key.trim() };
	if (pending) return { pending: true };
	return null;
}

export default { validate, activate, deactivate, track, feedback, getPlans, consumeActivationFromUrl, consentAllowed, ACTIVATE_PARAM, PENDING_PARAM };
